import { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography, makeStyles } from '@material-ui/core';
import http from '../../../../../utility/http';
import { api_one_on_one_with_view_permission } from '../../../../../constants/apiRoutes';
import UserCount from '../../../userCount';
import OneOnOneTimeIndicator from '../../../oneOnOneTimeIndicator';
import ActionButton from '../../../actionButton';
import OneOnOneCalendar from '../../../oneOnOneCalendar';
import Impression from '../../../impressions/impression';
import UserLink from '../../../userLink';
import DotsMenu from '../../../dotsMenu';
import TasksIconTooltip from '../../../tasksIconTooltip';
import PlansIconTooltip from '../../../plansIconTooltip';
import RecurringIconTooltip from '../../../recuringIconTooltip';
import RequestedIconTooltip from '../../../requestedIconTooltip';
import PrivateNote from '../privateNote';
import UpdatesInfo from '../../../updatesInfo';
import Tooltip from '../../../tooltip';
import { ReactComponent as PreviewUserIcon } from '../../../../../assets/icons/remove_red_eye.svg';
import {
  TIME_FORMAT,
  DATE_FORMAT,
  RECURRING_TYPES_MAP,
} from '../../../../../constants/oneOnOne';
import { prepareDotsMenuItems } from '../../../../../utility/oneOnOne';
import {
  getContentLength,
  getPersonalAttributeTooltipText,
} from '../../../../../utility/uiUtils';
import { isArrayEmpty } from '../../../../../utility/helpers';
import { formatDate } from '../../../customDateRange/config';
import { ACTION_BUTTON_TYPES } from '../../../actionButton/config';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    padding: spacing(6, 5, 4, 5),
    position: 'relative',
  },
  statusWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: spacing(2),
  },
  actionsWrapper: {
    display: 'flex',
  },
  rescheduleWrapper: {
    position: 'absolute',
    top: 22,
    right: 50,
  },
  dateWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: spacing(1),
    height: 24,
  },
  date: {
    marginRight: spacing(1.5),
  },
  time: {
    marginBottom: spacing(4),
  },
  impression: {
    marginBottom: spacing(4),
  },
  impressionLabel: {
    color: primary.bluish4,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: spacing(2),
  },
  impressionTooltip: {
    width: 12,
    height: 12,
    marginLeft: spacing(1),
  },
  impressionLabelWrapper: {
    display: 'flex',
  },
  impressionValue: {
    cursor: 'default',
  },
  autoSpacing: {
    marginTop: 'auto',
    maxWidth: 'calc(100% - 26px)',
  },
  creator: {
    color: primary.bluish4,
    fontFamily: 'ProximaNova-Bold',
    fontSize: 14,
    lineHeight: '18px',
  },
  dotsMenu: {
    position: 'absolute',
    right: 20,
    top: 24,
  },
  userCount: {
    position: 'absolute',
    right: 18,
    bottom: 13,
  },
  spacingBottom: {
    marginBottom: spacing(4),
  },
  iconsWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  iconSpacing: {
    marginRight: spacing(2),
  },
  notifications: {
    position: 'absolute',
    bottom: 47,
    right: 18,
  },
  iconTooltip: {
    marginLeft: spacing(1.5),
  },
  accessible: {
    cursor: 'pointer',
  },
}));

const Info = ({
  translations,
  record,
  notifications = [],
  canEdit,
  canDelete,
  canAccessOneOnOne = false,
  isFuture = false,
  hasRescheduleButton = false,
  hasNotifications = false,
  isNotificationUpdate = false,
  isDisabled = false,
  noteLabelComponent,
  onReschedule = () => {},
  onAddImpression = () => {},
  onEdit = () => {},
  onDelete = () => {},
  onManageTasks = () => {},
  goToUserProfilePage,
  canAccessUserProfileCheck,
  onClearNotifications,
}) => {
  const classes = useStyles();
  const {
    moderator,
    meeting_time,
    has_impression,
    requested,
    meeting_impression,
    isCreatedByMe,
    isCreatedForMe,
    note,
    canAccessModeratorProfile,
    recurring,
    tasks,
    plans,
  } = record;
  const hasAddToCalendarButton = isFuture && (isCreatedByMe || isCreatedForMe);
  const isRecurring = recurring !== RECURRING_TYPES_MAP.DOES_NOT_REPEAT.id;
  const hasActions = !has_impression && isCreatedByMe && !isDisabled;
  const hasPlans = !isArrayEmpty(plans);
  const hasTasks = !isArrayEmpty(tasks);
  const hasPrivateNote =
    isCreatedByMe && note && getContentLength(note?.text) > 0;

  const handleRescedule = useCallback(
    e => {
      e.stopPropagation();
      onReschedule();
    },
    [onReschedule]
  );

  const handleAddImpression = useCallback(
    e => {
      e.stopPropagation();
      onAddImpression();
    },
    [onAddImpression]
  );

  const getUsersWithViewPermission = recordId => () =>
    http.get(api_one_on_one_with_view_permission(recordId));

  return (
    <div className={classes.root}>
      <div className={classes.statusWrapper}>
        <OneOnOneTimeIndicator
          labels={translations.statuses}
          meetingTime={meeting_time}
          hasImpression={has_impression}
        />
        {requested && (
          <RequestedIconTooltip
            className={classNames(classes.iconTooltip, {
              [classes.accessible]: canAccessOneOnOne,
            })}
            labels={translations.requested}
          />
        )}
        <RecurringIconTooltip
          className={classes.iconTooltip}
          iconClass={classNames({
            [classes.accessible]: canAccessOneOnOne,
          })}
          isVisible={isRecurring}
          text={translations.recurring}
        />
        {hasRescheduleButton && hasActions && (
          <div
            className={classNames(
              classes.actionsWrapper,
              classes.rescheduleWrapper
            )}
          >
            <ActionButton
              type={ACTION_BUTTON_TYPES.RESCHEDULE}
              tooltipText={translations.actionButtons.reschedule}
              onClickHandler={handleRescedule}
              isSquared
            />
          </div>
        )}
      </div>
      <div>
        <div className={classes.dateWrapper}>
          <Typography className={classes.date} variant="subtitle1">
            {formatDate(meeting_time, DATE_FORMAT)}
          </Typography>
          {hasAddToCalendarButton ? (
            <OneOnOneCalendar
              translations={translations.calendar}
              record={record}
              isSmall
            />
          ) : null}
        </div>
        <Typography variant="caption" className={classes.time}>
          {formatDate(meeting_time, TIME_FORMAT)}
        </Typography>
      </div>
      {!isFuture && (
        <div className={classes.impression}>
          {(hasActions || !!meeting_impression) && (
            <div className={classes.impressionLabelWrapper}>
              <Typography
                variant="overline"
                className={classes.impressionLabel}
              >
                {translations.generalImpression}
              </Typography>
              <Tooltip
                customIconClass={classes.impressionTooltip}
                text={getPersonalAttributeTooltipText(
                  translations.impressionTooltip,
                  record.user
                )}
              />
            </div>
          )}
          {hasActions && (
            <div className={classes.actionsWrapper}>
              <ActionButton
                type={ACTION_BUTTON_TYPES.RESOLVE}
                tooltipText={translations.actionButtons.resolve}
                onClickHandler={handleAddImpression}
                isSquared
              />
            </div>
          )}
          {!!meeting_impression && (
            <Impression
              className={classes.impressionValue}
              value={meeting_impression}
              isSmall
            />
          )}
        </div>
      )}
      <div
        className={classNames(classes.iconsWrapper, {
          [classes.spacingBottom]: hasPrivateNote || hasTasks,
        })}
      >
        <PlansIconTooltip
          className={classes.iconSpacing}
          iconClass={classNames({
            [classes.accessible]: canAccessOneOnOne,
          })}
          text={translations.plansIconTooltip}
          isVisible={hasPlans}
          numberOfPlans={plans.length}
        />
        <TasksIconTooltip
          className={classes.iconSpacing}
          iconClass={classNames({
            [classes.accessible]: canAccessOneOnOne,
          })}
          text={translations.tasksIconTooltip}
          isVisible={hasTasks}
          numberOfTasks={tasks.length}
        />
        {hasPrivateNote && (
          <PrivateNote
            title={translations.note}
            helpTooltip={translations.viewPrivateNote}
            note={note.text}
            itemComponent={noteLabelComponent}
          />
        )}
      </div>
      <UserLink
        className={classes.autoSpacing}
        labelClass={classes.creator}
        label={translations.createdBy}
        meLabel={translations.createdByMe}
        user={moderator}
        isCurrentUser={isCreatedByMe}
        isUserAccessible={canAccessModeratorProfile}
      />
      <UserCount
        className={classes.userCount}
        icon={PreviewUserIcon}
        tooltipTitle={translations.usersListTitle}
        tooltipLabel={translations?.usersListTooltip}
        hasUserCount={false}
        canAccessUserProfileCheck={canAccessUserProfileCheck}
        goToUserProfile={goToUserProfilePage}
        getUsers={getUsersWithViewPermission(record.id)}
        isPreviewIcon
      />
      <UpdatesInfo
        className={classes.notifications}
        translations={translations.notifications}
        updates={notifications}
        isUpdate={isNotificationUpdate}
        hasUpdates={hasNotifications}
        onClearAll={onClearNotifications}
      />
      {(canEdit || canDelete || isCreatedByMe || isCreatedForMe) && (
        <DotsMenu
          className={classes.dotsMenu}
          menuItems={prepareDotsMenuItems(
            translations,
            isCreatedByMe,
            isCreatedForMe,
            isDisabled,
            canEdit,
            canDelete,
            false,
            onEdit,
            onDelete,
            onManageTasks
          )}
        />
      )}
    </div>
  );
};

Info.propTypes = {
  translations: PropTypes.object.isRequired,
  record: PropTypes.object.isRequired,
  notifications: PropTypes.arrayOf(PropTypes.string),
  isNotificationUpdate: PropTypes.bool,
  hasNotifications: PropTypes.bool,
  goToUserProfilePage: PropTypes.func.isRequired,
  canAccessUserProfileCheck: PropTypes.func.isRequired,
  hasRescheduleButton: PropTypes.bool,
  isFuture: PropTypes.bool,
  canAccessOneOnOne: PropTypes.bool,
  isDisabled: PropTypes.bool,
  noteLabelComponent: PropTypes.func.isRequired,
  onReschedule: PropTypes.func,
  onAddImpression: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onManageTasks: PropTypes.func,
  onClearNotifications: PropTypes.func.isRequired,
};

export default Info;
